import React, { useState, useCallback, useRef, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { supabase } from '../../config/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import { videoRecorderService } from '../../services/videoRecorderService';
import { RecordingMetadata } from '../../types/videoRecorder';
import { logger } from '../../utils/logger';

interface Props {
  onVideoSaved: (videoId: string) => void;
  onCancel: () => void;
  maxDurationSeconds?: number;
}

type Stage = 'idle' | 'countdown' | 'recording' | 'paused' | 'preview' | 'saving';

const ScreenShareRecorder: React.FC<Props> = ({ onVideoSaved, onCancel, maxDurationSeconds = 300 }) => {
  const { user } = useAuth();

  const [stage, setStage] = useState<Stage>('idle');
  const [supportMessage, setSupportMessage] = useState<string>('');
  const [timerMs, setTimerMs] = useState(0);
  const [title, setTitle] = useState('New Recording');
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [showWebcam, setShowWebcam] = useState<boolean>(() => {
    const v = localStorage.getItem('rec.showWebcam');
    return v === null ? true : v === 'true';
  });
  const [includeSystemAudio, setIncludeSystemAudio] = useState<boolean>(() => {
    const v = localStorage.getItem('rec.includeSystemAudio');
    return v === null ? true : v === 'true';
  });
  const [micDevices, setMicDevices] = useState<MediaDeviceInfo[]>([]);
  const [camDevices, setCamDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedMicId, setSelectedMicId] = useState<string | 'default' | ''>(localStorage.getItem('rec.selectedMicId') || 'default');
  const [selectedCamId, setSelectedCamId] = useState<string | 'default' | ''>(localStorage.getItem('rec.selectedCamId') || 'default');
  type BubbleSize = 'S' | 'M' | 'L';
  type BubbleCorner = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  // Back-compat: derive initial scale from legacy dropdown if present
  const legacySize = ((localStorage.getItem('rec.bubbleSize') as BubbleSize) || 'M');
  const legacyScale = legacySize === 'S' ? 0.16 : legacySize === 'L' ? 0.28 : 0.22;
  const parsedScale = parseFloat(localStorage.getItem('rec.bubbleScale') || '');
  const [bubbleScale, setBubbleScale] = useState<number>(Number.isFinite(parsedScale) ? Math.min(0.5, Math.max(0.15, parsedScale)) : legacyScale);
  const [bubbleCorner, setBubbleCorner] = useState<BubbleCorner>((localStorage.getItem('rec.bubbleCorner') as BubbleCorner) || 'bottom-right');
  const initialOffsetX = parseFloat(localStorage.getItem('rec.bubbleOffsetX') || '0') || 0;
  const initialOffsetY = parseFloat(localStorage.getItem('rec.bubbleOffsetY') || '0') || 0;
  const [bubbleOffsetPct, setBubbleOffsetPct] = useState<{x:number;y:number}>({ x: initialOffsetX, y: initialOffsetY });
  
  // Video quality settings
  type VideoQuality = '720p' | '1080p' | '4K';
  const qualityPresets = {
    '720p': { width: 1280, height: 720, label: 'HD (720p)' },
    '1080p': { width: 1920, height: 1080, label: 'Full HD (1080p)' },
    '4K': { width: 3840, height: 2160, label: '4K (2160p)' }
  };
  const [videoQuality, setVideoQuality] = useState<VideoQuality>(
    (localStorage.getItem('rec.videoQuality') as VideoQuality) || '1080p'
  );
  
  // Background blur settings
  const [enableBackgroundBlur, setEnableBackgroundBlur] = useState(
    localStorage.getItem('rec.backgroundBlur') === 'true'
  );
  const [blurAmount, setBlurAmount] = useState(
    parseInt(localStorage.getItem('rec.blurAmount') || '10')
  );

  const displayStreamRef = useRef<MediaStream | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const camStreamRef = useRef<MediaStream | null>(null);
  const previewStreamRef = useRef<MediaStream | null>(null); // Separate stream for preview display
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const startTimeRef = useRef<number>(0);
  const timerIntervalRef = useRef<number | null>(null);
  const countdownIntervalRef = useRef<number | null>(null);
  const [countdownValue, setCountdownValue] = useState<number | null>(null);
  const [isMicMuted, setIsMicMuted] = useState<boolean>(false);
  const [selectedThumbDataUrl, setSelectedThumbDataUrl] = useState<string | null>(null);
  const [micLevel, setMicLevel] = useState<number>(0);

  const previewVideoRef = useRef<HTMLVideoElement | null>(null);
  const screenVideoRef = useRef<HTMLVideoElement | null>(null);
  const camVideoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const drawIntervalRef = useRef<number | null>(null);
  const frameCountRef = useRef<number>(0);
  const showWebcamRef = useRef<boolean>(showWebcam);
  const bubbleScaleRef = useRef<number>(bubbleScale);
  const bubbleCornerRef = useRef<BubbleCorner>(bubbleCorner);
  const bubbleOffsetPctRef = useRef<{ x: number; y: number }>(bubbleOffsetPct);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const micSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const micLevelAnimRef = useRef<number | null>(null);

  // Draggable handle state
  const [isDragging, setIsDragging] = useState(false);
  const dragLastPointRef = useRef<{x:number;y:number} | null>(null);
  const [viewport, setViewport] = useState<{w:number; h:number}>({ w: window.innerWidth, h: window.innerHeight });
  const [showHandle, setShowHandle] = useState(true);
  const handleHideTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Auto-hide handle after inactivity
  useEffect(() => {
    const showThenHide = () => {
      setShowHandle(true);
      if (handleHideTimerRef.current) window.clearTimeout(handleHideTimerRef.current);
      handleHideTimerRef.current = window.setTimeout(() => setShowHandle(false), 1600);
    };
    const onMove = () => {
      if (!isDragging) showThenHide();
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('touchstart', showThenHide, { passive: true } as any);
    // initial hide schedule
    showThenHide();
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('touchstart', showThenHide as any);
      if (handleHideTimerRef.current) window.clearTimeout(handleHideTimerRef.current);
      handleHideTimerRef.current = null;
    };
  }, [isDragging]);

  // Feature detection
  useEffect(() => {
    const hasGetDisplay = !!(navigator.mediaDevices && (navigator.mediaDevices as any).getDisplayMedia);
    if (!hasGetDisplay) {
      setSupportMessage('Screen recording not supported in this browser. Use Chrome/Edge for best results.');
    }
  }, []);

  useEffect(() => {
    showWebcamRef.current = showWebcam;
  }, [showWebcam]);

  useEffect(() => {
    bubbleScaleRef.current = bubbleScale;
  }, [bubbleScale]);

  useEffect(() => {
    bubbleCornerRef.current = bubbleCorner;
  }, [bubbleCorner]);

  useEffect(() => {
    bubbleOffsetPctRef.current = bubbleOffsetPct;
  }, [bubbleOffsetPct]);

  // Enumerate devices (mic/camera)
  useEffect(() => {
    const loadDevices = async () => {
      try {
        // Some browsers require permission before labels/devices are fully available
        await navigator.mediaDevices.getUserMedia({ audio: true, video: true }).then(stream => {
          stream.getTracks().forEach(t => t.stop());
        }).catch(() => {});
        const list = await navigator.mediaDevices.enumerateDevices();
        setMicDevices(list.filter(d => d.kind === 'audioinput'));
        setCamDevices(list.filter(d => d.kind === 'videoinput'));
      } catch (e) {
        // ignore; UI will still show defaults
      }
    };
    if (navigator.mediaDevices && typeof navigator.mediaDevices.enumerateDevices === 'function') {
      loadDevices();
    }
  }, []);

  const clearTimer = () => {
    if (timerIntervalRef.current) {
      window.clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  };

  /**
   * Clone a MediaStream for preview purposes to avoid conflicts with recording
   * This prevents preview blanking during recording state changes
   */
  const cloneStreamForPreview = (originalStream: MediaStream): MediaStream => {
    try {
      const clonedTracks = originalStream.getTracks().map(track => track.clone());
      const clonedStream = new MediaStream(clonedTracks);
      
      logger.debug('Cloned stream for preview', {
        originalTracks: originalStream.getTracks().length,
        clonedTracks: clonedTracks.length
      });
      
      // Add event listeners to handle track ending
      clonedTracks.forEach(track => {
        track.addEventListener('ended', () => {
          logger.debug('Preview track ended', { kind: track.kind });
        });
      });
      
      return clonedStream;
    } catch (err) {
      logger.error('Failed to clone stream for preview', err);
      // Fallback to using original stream if cloning fails
      return originalStream;
    }
  };

  const stopStreams = () => {
    displayStreamRef.current?.getTracks().forEach(t => t.stop());
    micStreamRef.current?.getTracks().forEach(t => t.stop());
    camStreamRef.current?.getTracks().forEach(t => t.stop());
    previewStreamRef.current?.getTracks().forEach(t => t.stop());
    if (drawIntervalRef.current) {
      window.clearInterval(drawIntervalRef.current);
      drawIntervalRef.current = null;
    }
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    if (screenVideoRef.current) {
      try { (screenVideoRef.current as any).srcObject = null; } catch {}
    }
    if (camVideoRef.current) {
      try { (camVideoRef.current as any).srcObject = null; } catch {}
    }
    displayStreamRef.current = null;
    micStreamRef.current = null;
    camStreamRef.current = null;
    previewStreamRef.current = null;
    logger.debug('All streams stopped and cleaned up');
  };

  const startTimer = () => {
    startTimeRef.current = Date.now();
    setTimerMs(0);
    clearTimer();
    timerIntervalRef.current = window.setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      setTimerMs(elapsed);
      if (elapsed >= maxDurationSeconds * 1000) {
        handleStop();
      }
    }, 200);
  };

  const buildRecorder = (stream: MediaStream) => {
    // Validate stream has active tracks
    const videoTracks = stream.getVideoTracks();
    const audioTracks = stream.getAudioTracks();
    
    if (videoTracks.length === 0) {
      logger.error('Stream has no video tracks');
      throw new Error('Cannot record: stream has no video tracks');
    }
    
    logger.debug('Building MediaRecorder', { 
      videoTracks: videoTracks.length, 
      audioTracks: audioTracks.length,
      videoTrackState: videoTracks[0]?.readyState
    });
    
    // Try best codecs first with comprehensive fallbacks
    const tryTypes = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm;codecs=vp9',
      'video/webm;codecs=vp8',
      'video/webm',
      'video/mp4', // Safari fallback
    ];
    
    let recorder: MediaRecorder | null = null;
    let selectedType: string | null = null;
    
    for (const type of tryTypes) {
      if (MediaRecorder.isTypeSupported(type)) {
        try {
          recorder = new MediaRecorder(stream, { 
            mimeType: type, 
            bitsPerSecond: 4_000_000 
          });
          selectedType = type;
          logger.info('MediaRecorder created', { mimeType: type, bitrate: 4_000_000 });
          break;
        } catch (e) {
          logger.warn('Failed to create recorder with type', { type, error: e });
          continue;
        }
      }
    }
    
    if (!recorder) {
      // Last resort: create with default settings
      logger.warn('No supported MIME types found, using default');
      recorder = new MediaRecorder(stream);
      selectedType = 'default';
    }
    
    // Add error handler
    recorder.onerror = (event: Event) => {
      logger.error('MediaRecorder error', { 
        error: event, 
        state: recorder?.state,
        mimeType: selectedType
      });
      toast.error('Recording error occurred. Please try again.');
    };
    
    return recorder;
  };

  // Countdown state for canvas overlay
  const countdownStateRef = useRef<{ active: boolean; value: number }>({ active: false, value: 5 });

  const startDrawLoop = (canvas: HTMLCanvasElement, screenEl: HTMLVideoElement, camEl?: HTMLVideoElement) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    frameCountRef.current = 0;

    if (drawIntervalRef.current) {
      window.clearInterval(drawIntervalRef.current);
      drawIntervalRef.current = null;
    }

    const draw = () => {
      // Draw screen
      try {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        if (screenEl.readyState >= 2) {
          ctx.drawImage(screenEl, 0, 0, canvas.width, canvas.height);
          frameCountRef.current++;
        }
        // Draw webcam bubble if enabled and available
        if (showWebcamRef.current && camEl && camEl.readyState >= 2) {
          const sizeFactor = Math.min(0.5, Math.max(0.15, bubbleScaleRef.current ?? 0.22));
          const bubblePx = Math.round(Math.min(canvas.width, canvas.height) * sizeFactor);
          const padding = Math.round(bubblePx * 0.18);
          let cx = padding + bubblePx / 2;
          let cy = padding + bubblePx / 2;
          const corner = bubbleCornerRef.current;
          if (corner.includes('right')) cx = canvas.width - padding - bubblePx / 2;
          if (corner.includes('bottom')) cy = canvas.height - padding - bubblePx / 2;

          // Apply offset in canvas space
          const offset = bubbleOffsetPctRef.current;
          const ox = (offset?.x || 0) * canvas.width;
          const oy = (offset?.y || 0) * canvas.height;
          cx += ox; cy += oy;
          // Clamp to bounds keeping padding
          const minX = padding + bubblePx / 2;
          const maxX = canvas.width - padding - bubblePx / 2;
          const minY = padding + bubblePx / 2;
          const maxY = canvas.height - padding - bubblePx / 2;
          cx = Math.max(minX, Math.min(maxX, cx));
          cy = Math.max(minY, Math.min(maxY, cy));

          ctx.save();
          ctx.beginPath();
          ctx.arc(cx, cy, bubblePx / 2, 0, Math.PI * 2);
          ctx.closePath();
          ctx.shadowColor = 'rgba(0,0,0,0.25)';
          ctx.shadowBlur = 12;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 4;
          ctx.clip();
          ctx.drawImage(
            camEl,
            cx - bubblePx / 2,
            cy - bubblePx / 2,
            bubblePx,
            bubblePx
          );
          ctx.restore();

          // Border ring
          ctx.save();
          ctx.beginPath();
          ctx.arc(cx, cy, bubblePx / 2, 0, Math.PI * 2);
          ctx.strokeStyle = 'rgba(255,255,255,0.95)';
          ctx.lineWidth = Math.max(2, Math.round(bubblePx * 0.03));
          ctx.stroke();
          ctx.restore();
        }
      } catch (e) {
        // draw errors should not crash loop
      }
      
      if (countdownStateRef.current.active && countdownStateRef.current.value > 0) {
        try {
          ctx.save();
          
          // Semi-transparent black overlay
          ctx.fillStyle = 'rgba(0, 0, 0, 0.85)';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          
          // Draw giant countdown number
          const fontSize = Math.floor(canvas.height * 0.35);
          ctx.font = `bold ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = 'white';
          ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
          ctx.shadowBlur = 30;
          ctx.fillText(
            countdownStateRef.current.value.toString(), 
            canvas.width / 2, 
            canvas.height / 2
          );
          
          // Draw text below
          ctx.shadowBlur = 15;
          ctx.font = `${Math.floor(canvas.height * 0.04)}px Arial`;
          ctx.fillText(
            'Recording starts in...', 
            canvas.width / 2, 
            canvas.height * 0.7
          );
          
          ctx.restore();
        } catch (e) {
          console.error('Countdown draw error:', e);
        }
      }
    };

    draw();
    drawIntervalRef.current = window.setInterval(draw, 33);
  };

  const startRecording = useCallback(async () => {
    if (!user) {
      toast.error('Please log in to record');
      return;
    }

    try {
      // First, clear any previous recording
      setRecordedBlob(null);
      chunksRef.current = [];

      // Request screen with system audio and quality constraints FIRST
      const qualitySettings = qualityPresets[videoQuality];
      const displayStream: MediaStream = await (navigator.mediaDevices as any).getDisplayMedia({
        video: {
          width: { ideal: qualitySettings.width },
          height: { ideal: qualitySettings.height },
          frameRate: { ideal: 30 }
        },
        audio: includeSystemAudio
      });
      
      logger.debug('Display stream acquired', {
        quality: videoQuality,
        requestedResolution: `${qualitySettings.width}x${qualitySettings.height}`,
        actualSettings: displayStream.getVideoTracks()[0]?.getSettings()
      });
      displayStreamRef.current = displayStream;

      try {
        const vt = displayStream.getVideoTracks?.()[0];
        if (vt) {
          vt.addEventListener('ended', () => {
            try {
              if (recorderRef.current && recorderRef.current.state !== 'inactive') {
                handleStop();
              } else {
                stopStreams();
                setStage('idle');
              }
            } catch {
            }
          });
        }
      } catch {
      }

      // Try get microphone as a separate stream (optional)
      try {
        const audio: MediaStreamConstraints['audio'] = selectedMicId && selectedMicId !== 'default' ? { deviceId: { exact: selectedMicId } } : true;
        const mic = await navigator.mediaDevices.getUserMedia({ audio, video: false });
        micStreamRef.current = mic;
        // Setup mic meter
        try {
          if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
          const ctx = audioCtxRef.current!;
          const source = ctx.createMediaStreamSource(mic);
          const analyser = ctx.createAnalyser();
          analyser.fftSize = 512;
          source.connect(analyser);
          analyserRef.current = analyser;
          micSourceRef.current = source;
          const data = new Uint8Array(analyser.frequencyBinCount);
          const update = () => {
            analyser.getByteTimeDomainData(data);
            // RMS
            let sum = 0;
            for (let i = 0; i < data.length; i++) {
              const v = (data[i] - 128) / 128;
              sum += v * v;
            }
            const rms = Math.sqrt(sum / data.length);
            setMicLevel(Math.min(1, rms * 1.5));
            micLevelAnimRef.current = requestAnimationFrame(update);
          };
          micLevelAnimRef.current = requestAnimationFrame(update);
        } catch {}
        // If display has no audio, add mic track to display stream so we record both
        const hasDisplayAudio = includeSystemAudio && displayStream.getAudioTracks().length > 0;
        if (!hasDisplayAudio && mic.getAudioTracks().length > 0) {
          mic.getAudioTracks().forEach(track => displayStream.addTrack(track));
        }
      } catch (e) {
        // mic permission denied or unavailable
      }

      // Try get webcam video (no audio to avoid duplicate mic)
      try {
        if (showWebcam) {
          const video: MediaStreamConstraints['video'] = selectedCamId && selectedCamId !== 'default' ? { deviceId: { exact: selectedCamId } } : true;
          const cam = await navigator.mediaDevices.getUserMedia({ video, audio: false });
          camStreamRef.current = cam;
        }
      } catch (e) {
        // camera permission denied or unavailable
        camStreamRef.current = null;
        toast.error('Webcam unavailable. Continuing without webcam.');
      }

      // SIMPLIFIED APPROACH: Record display stream directly
      // Canvas compositing was causing issues with multi-screen setups
      logger.info('Using direct display stream recording (no canvas compositing)');

      const buildDirectStream = () => {
        const direct = new MediaStream();
        const displayVideoTrack = displayStreamRef.current!.getVideoTracks()[0];
        if (displayVideoTrack) {
          direct.addTrack(displayVideoTrack);
          logger.debug('Added display video track', {
            id: displayVideoTrack.id,
            label: displayVideoTrack.label,
            state: displayVideoTrack.readyState
          });
        }

        const displayAudios = displayStreamRef.current?.getAudioTracks() || [];
        displayAudios.forEach(t => {
          direct.addTrack(t);
          logger.debug('Added display audio track', { id: t.id });
        });

        if (micStreamRef.current && micStreamRef.current.getAudioTracks().length > 0) {
          const micTrack = micStreamRef.current.getAudioTracks()[0];
          if (!displayAudios.find(t => t.id === micTrack.id)) {
            direct.addTrack(micTrack);
            logger.debug('Added microphone track', { id: micTrack.id });
          }
        }

        return direct;
      };

      let mixed: MediaStream | null = null;

      try {
        const canvas = canvasRef.current;
        const screenEl = screenVideoRef.current;
        if (canvas && screenEl) {
          const displayVideoTrack = displayStream.getVideoTracks?.()[0];
          const settings: any = displayVideoTrack?.getSettings?.() || {};
          canvas.width = settings.width || qualitySettings.width;
          canvas.height = settings.height || qualitySettings.height;

          (screenEl as any).srcObject = displayStream;
          try { await screenEl.play(); } catch {}

          let camEl: HTMLVideoElement | undefined;
          if (showWebcam && camStreamRef.current && camVideoRef.current) {
            camEl = camVideoRef.current;
            (camEl as any).srcObject = camStreamRef.current;
            try { await camEl.play(); } catch {}
          }

          startDrawLoop(canvas, screenEl, camEl);

          await new Promise<void>((resolve) => window.setTimeout(resolve, 350));

          if (frameCountRef.current > 0) {
            const canvasStream = canvas.captureStream(30);
            const composed = new MediaStream();

            const canvasVideo = canvasStream.getVideoTracks()[0];
            if (canvasVideo) composed.addTrack(canvasVideo);

            const seen = new Set<string>();
            const addAudio = (t?: MediaStreamTrack) => {
              if (!t) return;
              if (t.readyState !== 'live') return;
              if (seen.has(t.id)) return;
              composed.addTrack(t);
              seen.add(t.id);
            };

            (micStreamRef.current?.getAudioTracks?.() || []).forEach(addAudio);
            if (includeSystemAudio) {
              (displayStream.getAudioTracks?.() || []).forEach(addAudio);
            }
            if (composed.getAudioTracks().length === 0) {
              (displayStream.getAudioTracks?.() || []).forEach(addAudio);
            }

            if (composed.getVideoTracks().length > 0) {
              mixed = composed;
            }
          }
        }
      } catch {
        mixed = null;
      }

      if (!mixed) {
        mixed = buildDirectStream();
      }
      
      // Create preview stream
      previewStreamRef.current = cloneStreamForPreview(mixed);
      logger.info('Recording stream prepared', {
        videoTracks: mixed.getVideoTracks().length,
        audioTracks: mixed.getAudioTracks().length
      });

      let recorder: MediaRecorder;
      try {
        chunksRef.current = [];
        recorder = buildRecorder(mixed);
        recorderRef.current = recorder;

        recorder.ondataavailable = (e: BlobEvent) => {
          if (e.data && e.data.size > 0) {
            chunksRef.current.push(e.data);
            logger.debug('Blob chunk received', { size: e.data.size, totalChunks: chunksRef.current.length });
          } else {
            logger.warn('Empty blob chunk received');
          }
        };

        recorder.onstop = () => {
          clearTimer();
          if (drawIntervalRef.current) {
            window.clearInterval(drawIntervalRef.current);
            drawIntervalRef.current = null;
          }
          if (animFrameRef.current) {
            cancelAnimationFrame(animFrameRef.current);
            animFrameRef.current = null;
          }

          // Enhanced blob validation
          const mimeType = recorder?.mimeType || 'video/webm';
          const totalSize = chunksRef.current.reduce((sum, chunk) => {
            if (chunk instanceof Blob) return sum + chunk.size;
            if (typeof chunk === 'string') return sum + chunk.length;
            if (chunk instanceof ArrayBuffer) return sum + chunk.byteLength;
            return sum;
          }, 0);
          logger.debug('MediaRecorder stopped', {
            chunks: chunksRef.current.length,
            mimeType,
            totalSize
          });

          if (chunksRef.current.length === 0) {
            logger.error('No chunks collected during recording');
            toast.error('Recording failed. No data captured. Try a different browser or check permissions.');
            chunksRef.current = [];
            setRecordedBlob(null);
            setStage('idle');
            stopStreams();
            return;
          }

          const blob = new Blob(chunksRef.current, { type: mimeType });
          logger.debug('Blob created', { size: blob.size, type: blob.type });

          if (!blob || blob.size === 0) {
            logger.error('Empty recording blob despite having chunks', {
              chunkCount: chunksRef.current.length
            });
            toast.error('Recording failed. Video file is empty.');
            chunksRef.current = [];
            setRecordedBlob(null);
            setStage('idle');
          } else if (blob.size < 1000) {
            logger.error('Blob size too small', { size: blob.size });
            toast.error('Recording failed. Video file is corrupted or too short.');
            chunksRef.current = [];
            setRecordedBlob(null);
            setStage('idle');
          } else {
            logger.info('Recording successful', { size: blob.size, duration: timerMs });
            setRecordedBlob(blob);
            setStage('preview');
          }
          stopStreams();
        };

        logger.info('Starting MediaRecorder');
        recorder.start(1000);
      } catch (err) {
        logger.error('Failed to start MediaRecorder', err);
        toast.error('Failed to start recording');
        setStage('idle');
        stopStreams();
        return;
      }
      
      // Show countdown
      setStage('countdown');
      countdownStateRef.current.active = true;
      countdownStateRef.current.value = 5;
      setCountdownValue(5);
      toast.success('Recording will start in 5 seconds...', { duration: 2000 });
      
      logger.info('Starting countdown');
      
      // Simple 3-second countdown
      await new Promise<void>((resolve) => {
        let count = 5;
        const interval = setInterval(() => {
          count--;
          setCountdownValue(count);
          countdownStateRef.current.value = count;
          logger.debug(`Countdown: ${count}`);
          
          if (count <= 0) {
            clearInterval(interval);
            countdownIntervalRef.current = null;
            countdownStateRef.current.active = false;
            setCountdownValue(null);
            logger.info('Countdown complete');
            resolve();
          }
        }, 1000);
        countdownIntervalRef.current = interval as any;
      });

      setStage('recording');
      startTimer();
    } catch (err: any) {
      logger.error('Screen recording initialization failed', err);
      if (err && err.name === 'NotAllowedError') {
        toast.error('Permission denied. Please allow screen recording.');
      } else {
        toast.error('Unable to start screen recording');
      }
      setStage('idle');
      stopStreams();
    }
  }, [user, maxDurationSeconds, showWebcam, selectedMicId, selectedCamId, bubbleScale, bubbleCorner, bubbleOffsetPct, includeSystemAudio]);

  const handlePause = () => {
    if (recorderRef.current && recorderRef.current.state === 'recording') {
      recorderRef.current.pause();
      clearTimer();
      setStage('paused');
    }
  };

  const handleResume = () => {
    if (recorderRef.current && recorderRef.current.state === 'paused') {
      recorderRef.current.resume();
      startTimer();
      setStage('recording');
    }
  };

  const toggleMicMute = () => {
    const track = micStreamRef.current?.getAudioTracks?.()[0];
    if (track) {
      track.enabled = !track.enabled;
      setIsMicMuted(!track.enabled);
    } else {
      toast.error('Microphone not available');
    }
  };

  const handleStop = () => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try {
        recorderRef.current.requestData();
      } catch {
      }
      recorderRef.current.stop();
    }
  };

  const handleDiscard = () => {
    clearTimer();
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try {
        recorderRef.current.stop();
      } catch {
      }
    }
    stopStreams();
    chunksRef.current = [];
    setRecordedBlob(null);
    setStage('idle');
  };

  const saveToSupabase = useCallback(async () => {
    if (!user || !recordedBlob) return;
    try {
      setStage('saving');

      // Convert blob to File for service API
      const filename = `${Date.now()}.webm`;
      const file = new File([recordedBlob], filename, { type: recordedBlob.type || 'video/webm' });

      const metadata: RecordingMetadata = {
        duration: Math.max(1, Math.round(timerMs / 1000)),
        fileSize: recordedBlob.size,
        hasAudio: true,
        hasVideo: true,
      };

      // Upload video and create DB row
      const recording = await videoRecorderService.uploadVideo(file, title, null, metadata);
      if (!recording) throw new Error('Upload failed');

      // Use selected thumbnail if available; otherwise generate at ~1s
      const thumbDataUrl = selectedThumbDataUrl || await videoRecorderService.generateThumbnail(file, 1);
      if (thumbDataUrl) {
        const path = await videoRecorderService.uploadThumbnail(recording.id, thumbDataUrl);
        if (!path) {
          toast.error('Saved video, but failed to upload thumbnail');
        }
      } else {
        toast.error('Saved video, but failed to generate thumbnail');
      }

      toast.success('Recording saved');
      // Auto-copy share link (respects current environment base URL)
      try {
        const baseUrl = process.env.NODE_ENV === 'production' ? 'https://explanatum.co.uk' : window.location.origin;
        const link = `${baseUrl}/videos/watch/${recording.id}`;
        await navigator.clipboard?.writeText(link);
        toast.success('Share link copied to clipboard');
      } catch {}
      onVideoSaved(recording.id);
      handleDiscard();
    } catch (e) {
      logger.error('Failed to save recording to Supabase', e);
      toast.error('Failed to save recording');
      setStage('preview');
    }
  }, [user, recordedBlob, title, timerMs, onVideoSaved, selectedThumbDataUrl]);

  const generateThumbnail = (blob: Blob): Promise<Blob | null> => {
    return new Promise((resolve) => {
      try {
        const video = document.createElement('video');
        const url = URL.createObjectURL(blob);
        video.src = url;
        video.muted = true;
        video.playsInline = true as any;
        video.currentTime = 0.1;
        video.onloadeddata = () => {
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth || 1280;
          canvas.height = video.videoHeight || 720;
          const ctx = canvas.getContext('2d');
          if (!ctx) { resolve(null); return; }
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((b) => {
            URL.revokeObjectURL(url);
            resolve(b);
          }, 'image/png');
        };
        video.onerror = () => {
          URL.revokeObjectURL(url);
          resolve(null);
        };
      } catch {
        resolve(null);
      }
    });
  };

  useEffect(() => {
    return () => {
      clearTimer();
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      stopStreams();
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        try { recorderRef.current.stop(); } catch {}
      }
      if (drawIntervalRef.current) {
        window.clearInterval(drawIntervalRef.current);
        drawIntervalRef.current = null;
      }
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = null;
      }
      if (micLevelAnimRef.current) {
        cancelAnimationFrame(micLevelAnimRef.current);
        micLevelAnimRef.current = null;
      }
      try {
        analyserRef.current?.disconnect();
        micSourceRef.current?.disconnect();
        // Only close AudioContext if it's not already closed
        if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
          audioCtxRef.current.close();
        }
      } catch (error) {
        // Ignore cleanup errors
      }
    };
  }, []);

  // Hotkeys: Space pause/resume, Esc cancel, Shift+W toggle webcam
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Avoid interfering with typing in inputs
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || (e.target as HTMLElement)?.isContentEditable) return;
      if (stage === 'recording' || stage === 'paused') {
        if (e.code === 'Space') {
          e.preventDefault();
          if (stage === 'recording') handlePause(); else handleResume();
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          handleDiscard();
        }
        if (e.shiftKey && (e.code === 'KeyW')) {
          e.preventDefault();
          setShowWebcam(prev => {
            const nv = !prev;
            localStorage.setItem('rec.showWebcam', String(nv));
            return nv;
          });
        }
        // Ctrl+Arrow: set bubble corner quickly (persist)
        if (e.ctrlKey && !e.shiftKey) {
          let next: BubbleCorner | null = null;
          if (e.key === 'ArrowLeft') next = bubbleCorner.includes('top') ? 'top-left' : bubbleCorner.includes('bottom') ? 'bottom-left' : 'bottom-left';
          if (e.key === 'ArrowRight') next = bubbleCorner.includes('top') ? 'top-right' : bubbleCorner.includes('bottom') ? 'bottom-right' : 'bottom-right';
          if (e.key === 'ArrowUp') next = bubbleCorner.includes('left') ? 'top-left' : bubbleCorner.includes('right') ? 'top-right' : 'top-right';
          if (e.key === 'ArrowDown') next = bubbleCorner.includes('left') ? 'bottom-left' : bubbleCorner.includes('right') ? 'bottom-right' : 'bottom-right';
          if (next) {
            e.preventDefault();
            setBubbleCorner(next);
            localStorage.setItem('rec.bubbleCorner', next);
          }
        }

        // Arrow key nudges for webcam bubble
        const step = e.shiftKey ? 0.05 : 0.02;
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          const nx = Math.max(-0.5, Math.min(0.5, bubbleOffsetPct.x - step));
          setBubbleOffsetPct({ ...bubbleOffsetPct, x: nx });
          localStorage.setItem('rec.bubbleOffsetX', String(nx));
        }
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          const nx = Math.max(-0.5, Math.min(0.5, bubbleOffsetPct.x + step));
          setBubbleOffsetPct({ ...bubbleOffsetPct, x: nx });
          localStorage.setItem('rec.bubbleOffsetX', String(nx));
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          const ny = Math.max(-0.5, Math.min(0.5, bubbleOffsetPct.y - step));
          setBubbleOffsetPct({ ...bubbleOffsetPct, y: ny });
          localStorage.setItem('rec.bubbleOffsetY', String(ny));
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          const ny = Math.max(-0.5, Math.min(0.5, bubbleOffsetPct.y + step));
          setBubbleOffsetPct({ ...bubbleOffsetPct, y: ny });
          localStorage.setItem('rec.bubbleOffsetY', String(ny));
        }
      }
      if (stage === 'countdown') {
        if (e.key === 'Escape') {
          e.preventDefault();
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          setCountdownValue(null);
          setStage('idle');
          stopStreams();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [stage, bubbleOffsetPct]);

  // Pointer drag handlers to adjust bubbleOffsetPct
  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => {
      const last = dragLastPointRef.current;
      if (!last) return;
      const dx = e.clientX - last.x;
      const dy = e.clientY - last.y;
      dragLastPointRef.current = { x: e.clientX, y: e.clientY };
      setBubbleOffsetPct(prev => {
        let nx = prev.x + dx / (window.innerWidth || 1);
        let ny = prev.y + dy / (window.innerHeight || 1);
        // Alt-drag grid snapping
        if (e.altKey) {
          const grid = 0.02; // 2% steps
          nx = Math.round(nx / grid) * grid;
          ny = Math.round(ny / grid) * grid;
        }
        nx = Math.max(-0.5, Math.min(0.5, nx));
        ny = Math.max(-0.5, Math.min(0.5, ny));
        localStorage.setItem('rec.bubbleOffsetX', String(nx));
        localStorage.setItem('rec.bubbleOffsetY', String(ny));
        return { x: nx, y: ny };
      });
    };
    const onUp = () => {
      setIsDragging(false);
      dragLastPointRef.current = null;
      // Snap to near edges for nicer alignment
      const margin = 96;
      let baseX = margin;
      let baseY = margin;
      if (bubbleCorner.includes('right')) baseX = viewport.w - margin;
      if (bubbleCorner.includes('bottom')) baseY = viewport.h - margin;
      const absX = baseX + (bubbleOffsetPct.x * viewport.w);
      const absY = baseY + (bubbleOffsetPct.y * viewport.h);
      const snapPx = 28;
      const nearLeft = absX < snapPx;
      const nearRight = (viewport.w - absX) < snapPx;
      const nearTop = absY < snapPx;
      const nearBottom = (viewport.h - absY) < snapPx;
      setBubbleOffsetPct(prev => {
        let nx = prev.x;
        let ny = prev.y;
        if (nearLeft) nx = (-baseX) / viewport.w; // push to left edge
        if (nearRight) nx = (viewport.w - baseX) / viewport.w; // push to right edge
        if (nearTop) ny = (-baseY) / viewport.h; // push to top edge
        if (nearBottom) ny = (viewport.h - baseY) / viewport.h; // push to bottom edge
        nx = Math.max(-0.5, Math.min(0.5, nx));
        ny = Math.max(-0.5, Math.min(0.5, ny));
        localStorage.setItem('rec.bubbleOffsetX', String(nx));
        localStorage.setItem('rec.bubbleOffsetY', String(ny));
        return { x: nx, y: ny };
      });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isDragging]);

  useEffect(() => {
    if (!isDragging) return;
    const onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t) return;
      const last = dragLastPointRef.current;
      if (!last) return;
      const dx = t.clientX - last.x;
      const dy = t.clientY - last.y;
      dragLastPointRef.current = { x: t.clientX, y: t.clientY };
      setBubbleOffsetPct(prev => {
        let nx = prev.x + dx / (window.innerWidth || 1);
        let ny = prev.y + dy / (window.innerHeight || 1);
        // Touch has no modifier; use grid always when moving slowly
        const grid = 0.02;
        if (Math.abs(dx) < 2 && Math.abs(dy) < 2) {
          nx = Math.round(nx / grid) * grid;
          ny = Math.round(ny / grid) * grid;
        }
        nx = Math.max(-0.5, Math.min(0.5, nx));
        ny = Math.max(-0.5, Math.min(0.5, ny));
        localStorage.setItem('rec.bubbleOffsetX', String(nx));
        localStorage.setItem('rec.bubbleOffsetY', String(ny));
        return { x: nx, y: ny };
      });
    };
    const onTouchEnd = () => {
      setIsDragging(false);
      dragLastPointRef.current = null;
      // Snap (same as mouse)
      const margin = 96;
      let baseX = margin;
      let baseY = margin;
      if (bubbleCorner.includes('right')) baseX = viewport.w - margin;
      if (bubbleCorner.includes('bottom')) baseY = viewport.h - margin;
      const absX = baseX + (bubbleOffsetPct.x * viewport.w);
      const absY = baseY + (bubbleOffsetPct.y * viewport.h);
      const snapPx = 28;
      const nearLeft = absX < snapPx;
      const nearRight = (viewport.w - absX) < snapPx;
      const nearTop = absY < snapPx;
      const nearBottom = (viewport.h - absY) < snapPx;
      setBubbleOffsetPct(prev => {
        let nx = prev.x;
        let ny = prev.y;
        if (nearLeft) nx = (-baseX) / viewport.w;
        if (nearRight) nx = (viewport.w - baseX) / viewport.w;
        if (nearTop) ny = (-baseY) / viewport.h;
        if (nearBottom) ny = (viewport.h - baseY) / viewport.h;
        nx = Math.max(-0.5, Math.min(0.5, nx));
        ny = Math.max(-0.5, Math.min(0.5, ny));
        localStorage.setItem('rec.bubbleOffsetX', String(nx));
        localStorage.setItem('rec.bubbleOffsetY', String(ny));
        return { x: nx, y: ny };
      });
    };
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener('touchcancel', onTouchEnd);
    return () => {
      window.removeEventListener('touchmove', onTouchMove as any);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [isDragging]);

  const minutes = Math.floor(timerMs / 60000).toString().padStart(2, '0');
  const seconds = Math.floor((timerMs % 60000) / 1000).toString().padStart(2, '0');

  return (
    <div className="bg-white rounded-lg shadow p-4">
      {supportMessage && (
        <div className="bg-yellow-50 text-yellow-800 border border-yellow-200 rounded p-2 mb-3 text-sm">
          {supportMessage}
        </div>
      )}

      {/* Controls */}
      {stage === 'idle' && (
        <div className="space-y-3">
          <div className="flex items-center gap-3 w-full">
            <input
              className="border rounded px-3 py-2 w-full max-w-lg"
              placeholder="Recording title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <label className="w-28 text-sm text-gray-600">Microphone</label>
              <select
                className="border rounded px-2 py-1 w-full"
                value={selectedMicId}
                onChange={(e) => {
                  setSelectedMicId(e.target.value as any);
                  localStorage.setItem('rec.selectedMicId', e.target.value);
                }}
              >
                <option value="default">System default</option>
                {micDevices.map(d => (
                  <option key={d.deviceId} value={d.deviceId}>{d.label || `Mic ${d.deviceId.substring(0,6)}`}</option>
                ))}
              </select>
              <div className="ml-2 h-2 flex-1 bg-gray-200 rounded overflow-hidden max-w-[160px]" title="Mic level">
                <div className="h-full bg-green-500 transition-[width]" style={{ width: `${Math.round(micLevel * 100)}%` }} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="w-28 text-sm text-gray-600">Camera</label>
              <select
                className="border rounded px-2 py-1 w-full"
                value={selectedCamId}
                onChange={(e) => {
                  setSelectedCamId(e.target.value as any);
                  localStorage.setItem('rec.selectedCamId', e.target.value);
                }}
              >
                <option value="default">System default</option>
                {camDevices.map(d => (
                  <option key={d.deviceId} value={d.deviceId}>{d.label || `Camera ${d.deviceId.substring(0,6)}`}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
            <label className="flex items-center text-sm text-gray-700 gap-2">
              <input
                type="checkbox"
                checked={showWebcam}
                onChange={(e) => {
                  setShowWebcam(e.target.checked);
                  localStorage.setItem('rec.showWebcam', String(e.target.checked));
                }}
              />
              Webcam bubble
            </label>
            <div className="flex items-center gap-3">
              <span className="w-24 text-sm text-gray-600">Bubble size</span>
              <input
                type="range"
                min={0.15}
                max={0.5}
                step={0.01}
                value={bubbleScale}
                onChange={(e) => {
                  const v = Math.min(0.5, Math.max(0.15, Number(e.target.value)));
                  setBubbleScale(v);
                  localStorage.setItem('rec.bubbleScale', String(v));
                  // Update legacy key approximately for back-compat with any older code paths
                  const legacy: BubbleSize = v <= 0.18 ? 'S' : v >= 0.27 ? 'L' : 'M';
                  localStorage.setItem('rec.bubbleSize', legacy);
                }}
                className="w-40"
              />
              <span className="text-xs text-gray-500 tabular-nums">{Math.round(bubbleScale * 100)}%</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-24 text-sm text-gray-600">Position</span>
              <select
                className="border rounded px-2 py-1"
                value={bubbleCorner}
                onChange={(e) => {
                  setBubbleCorner(e.target.value as BubbleCorner);
                  localStorage.setItem('rec.bubbleCorner', e.target.value);
                  // Reset offsets when changing anchor corner to avoid confusion
                  setBubbleOffsetPct({ x: 0, y: 0 });
                  localStorage.setItem('rec.bubbleOffsetX', '0');
                  localStorage.setItem('rec.bubbleOffsetY', '0');
                }}
              >
                <option value="bottom-right">Bottom right</option>
                <option value="bottom-left">Bottom left</option>
                <option value="top-right">Top right</option>
                <option value="top-left">Top left</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-24 text-sm text-gray-600">Nudge</span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  className="px-2 py-1 border rounded"
                  onClick={() => {
                    const nx = Math.max(-0.5, Math.min(0.5, (bubbleOffsetPct.x - 0.02)));
                    setBubbleOffsetPct({ ...bubbleOffsetPct, x: nx });
                    localStorage.setItem('rec.bubbleOffsetX', String(nx));
                  }}
                >←</button>
                <button
                  type="button"
                  className="px-2 py-1 border rounded"
                  onClick={() => {
                    const nx = Math.max(-0.5, Math.min(0.5, (bubbleOffsetPct.x + 0.02)));
                    setBubbleOffsetPct({ ...bubbleOffsetPct, x: nx });
                    localStorage.setItem('rec.bubbleOffsetX', String(nx));
                  }}
                >→</button>
                <button
                  type="button"
                  className="px-2 py-1 border rounded"
                  onClick={() => {
                    const ny = Math.max(-0.5, Math.min(0.5, (bubbleOffsetPct.y - 0.02)));
                    setBubbleOffsetPct({ ...bubbleOffsetPct, y: ny });
                    localStorage.setItem('rec.bubbleOffsetY', String(ny));
                  }}
                >↑</button>
                <button
                  type="button"
                  className="px-2 py-1 border rounded"
                  onClick={() => {
                    const ny = Math.max(-0.5, Math.min(0.5, (bubbleOffsetPct.y + 0.02)));
                    setBubbleOffsetPct({ ...bubbleOffsetPct, y: ny });
                    localStorage.setItem('rec.bubbleOffsetY', String(ny));
                  }}
                >↓</button>
                <button
                  type="button"
                  className="px-2 py-1 border rounded"
                  onClick={() => {
                    setBubbleOffsetPct({ x: 0, y: 0 });
                    localStorage.setItem('rec.bubbleOffsetX', '0');
                    localStorage.setItem('rec.bubbleOffsetY', '0');
                  }}
                >Reset</button>
              </div>
            </div>
          </div>

          {/* Video Quality Selector */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Video Quality
            </label>
            <div className="flex gap-2">
              {(Object.keys(qualityPresets) as VideoQuality[]).map((quality) => (
                <button
                  key={quality}
                  type="button"
                  onClick={() => {
                    setVideoQuality(quality);
                    localStorage.setItem('rec.videoQuality', quality);
                    logger.debug('Video quality changed', { quality });
                  }}
                  className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                    videoQuality === quality
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {qualityPresets[quality].label}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Current: {qualityPresets[videoQuality].width}x{qualityPresets[videoQuality].height}
              {videoQuality === '4K' && ' (requires powerful hardware)'}
            </p>
          </div>

          {/* Background Blur Controls */}
          {showWebcam && (
            <div className="mb-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div className="flex items-center justify-between mb-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enableBackgroundBlur}
                    onChange={(e) => {
                      setEnableBackgroundBlur(e.target.checked);
                      localStorage.setItem('rec.backgroundBlur', String(e.target.checked));
                      logger.debug('Background blur toggled', { enabled: e.target.checked });
                    }}
                    className="rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    🎭 Background Blur (Beta)
                  </span>
                </label>
                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                  Loom-style
                </span>
              </div>
              
              {enableBackgroundBlur && (
                <div>
                  <label className="block text-xs text-gray-600 mb-2">
                    Blur Amount: {blurAmount}
                  </label>
                  <input
                    type="range"
                    min="3"
                    max="20"
                    value={blurAmount}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      setBlurAmount(value);
                      localStorage.setItem('rec.blurAmount', String(value));
                    }}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Subtle</span>
                    <span>Strong</span>
                  </div>
                  <p className="text-xs text-purple-600 mt-2">
                    💡 Uses AI to blur your background professionally
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-4">
            <label className="flex items-center text-sm text-gray-700 gap-2">
              <input
                type="checkbox"
                checked={includeSystemAudio}
                onChange={(e) => {
                  setIncludeSystemAudio(e.target.checked);
                  localStorage.setItem('rec.includeSystemAudio', String(e.target.checked));
                }}
              />
              Include system audio
            </label>
          </div>

          {/* Camera Preview - Show BEFORE recording so user can see themselves */}
          {showWebcam && (
            <div className="mt-4 bg-black rounded-lg overflow-hidden border-2 border-blue-500">
              <div className="bg-blue-600 text-white text-sm py-2 px-3 font-medium">
                📹 Camera Preview - Make sure you're in frame!
              </div>
              <video
                ref={(el) => {
                  if (el) {
                    // Start camera preview
                    const startPreview = async () => {
                      try {
                        const constraints: MediaStreamConstraints['video'] = 
                          selectedCamId && selectedCamId !== 'default' 
                            ? { deviceId: { exact: selectedCamId } } 
                            : true;
                        const stream = await navigator.mediaDevices.getUserMedia({ 
                          video: constraints, 
                          audio: false 
                        });
                        el.srcObject = stream;
                        // Store for cleanup
                        camStreamRef.current = stream;
                      } catch (error) {
                        console.error('Camera preview failed:', error);
                      }
                    };
                    
                    if (!el.srcObject) {
                      startPreview();
                    }
                  }
                }}
                className="w-full max-h-80"
                autoPlay
                playsInline
                muted
              />
              <div className="bg-gray-800 text-gray-300 text-xs py-1 px-3 text-center">
                Position: {bubbleCorner.replace('-', ' ')} | Size: {Math.round(bubbleScale * 100)}%
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <button onClick={startRecording} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
              Start Recording
            </button>
          </div>
        </div>
      )}

      {/* Countdown is now drawn ON the canvas, not as React overlay */}
      {stage === 'countdown' && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-blue-600 text-white px-6 py-3 rounded-lg shadow-xl">
          <div className="text-center">
            <div className="text-sm font-medium">Recording starts in {countdownValue}...</div>
            <div className="text-xs text-blue-100 mt-1">Countdown visible on captured screen</div>
          </div>
        </div>
      )}

      {(stage === 'recording' || stage === 'paused') && (
        <>
          {/* Live preview of recording */}
          <div className="mb-4 bg-black rounded-lg overflow-hidden">
            <video
              ref={(el) => {
                if (el && previewStreamRef.current) {
                  el.srcObject = previewStreamRef.current;
                  el.play().catch(() => {});
                }
              }}
              className="w-full max-h-[50vh]"
              muted
              playsInline
            />
          </div>
          
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-gray-600">Duration: {minutes}:{seconds}</div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Live bubble controls */}
            <label className="flex items-center text-xs text-gray-700 gap-2 mr-2">
              <input
                type="checkbox"
                checked={showWebcam}
                onChange={(e) => {
                  setShowWebcam(e.target.checked);
                  localStorage.setItem('rec.showWebcam', String(e.target.checked));
                }}
              />
              Webcam
            </label>
            <div className="flex items-center gap-2 mr-2">
              <span className="text-xs text-gray-500">Size</span>
              <input
                type="range"
                min={0.15}
                max={0.5}
                step={0.01}
                value={bubbleScale}
                onChange={(e) => {
                  const v = Math.min(0.5, Math.max(0.15, Number(e.target.value)));
                  setBubbleScale(v);
                  localStorage.setItem('rec.bubbleScale', String(v));
                  const legacy = v <= 0.18 ? 'S' : v >= 0.27 ? 'L' : 'M';
                  localStorage.setItem('rec.bubbleSize', legacy);
                }}
                className="w-28"
              />
            </div>
            <div className="flex items-center gap-1 mr-2">
              <span className="text-xs text-gray-500">Pos</span>
              <select
                className="border rounded px-1 py-0.5 text-xs"
                value={bubbleCorner}
                onChange={(e) => {
                  setBubbleCorner(e.target.value as any);
                  localStorage.setItem('rec.bubbleCorner', e.target.value);
                  setBubbleOffsetPct({ x: 0, y: 0 });
                  localStorage.setItem('rec.bubbleOffsetX', '0');
                  localStorage.setItem('rec.bubbleOffsetY', '0');
                }}
              >
                <option value="bottom-right">BR</option>
                <option value="bottom-left">BL</option>
                <option value="top-right">TR</option>
                <option value="top-left">TL</option>
              </select>
            </div>
            <div className="flex items-center gap-1 mr-2">
              <span className="text-xs text-gray-500">Nudge</span>
              <button
                type="button"
                className="px-2 py-1 border rounded text-xs"
                onClick={() => {
                  const nx = Math.max(-0.5, Math.min(0.5, (bubbleOffsetPct.x - 0.02)));
                  setBubbleOffsetPct({ ...bubbleOffsetPct, x: nx });
                  localStorage.setItem('rec.bubbleOffsetX', String(nx));
                }}
              >←</button>
              <button
                type="button"
                className="px-2 py-1 border rounded text-xs"
                onClick={() => {
                  const nx = Math.max(-0.5, Math.min(0.5, (bubbleOffsetPct.x + 0.02)));
                  setBubbleOffsetPct({ ...bubbleOffsetPct, x: nx });
                  localStorage.setItem('rec.bubbleOffsetX', String(nx));
                }}
              >→</button>
              <button
                type="button"
                className="px-2 py-1 border rounded text-xs"
                onClick={() => {
                  const ny = Math.max(-0.5, Math.min(0.5, (bubbleOffsetPct.y - 0.02)));
                  setBubbleOffsetPct({ ...bubbleOffsetPct, y: ny });
                  localStorage.setItem('rec.bubbleOffsetY', String(ny));
                }}
              >↑</button>
              <button
                type="button"
                className="px-2 py-1 border rounded text-xs"
                onClick={() => {
                  const ny = Math.max(-0.5, Math.min(0.5, (bubbleOffsetPct.y + 0.02)));
                  setBubbleOffsetPct({ ...bubbleOffsetPct, y: ny });
                  localStorage.setItem('rec.bubbleOffsetY', String(ny));
                }}
              >↓</button>
              <button
                type="button"
                className="px-2 py-1 border rounded text-xs"
                onClick={() => {
                  setBubbleOffsetPct({ x: 0, y: 0 });
                  localStorage.setItem('rec.bubbleOffsetX', '0');
                  localStorage.setItem('rec.bubbleOffsetY', '0');
                }}
              >Reset</button>
            </div>

            {/* Mic meter */}
            <div className="hidden sm:flex items-center gap-2 mr-2">
              <span className="text-xs text-gray-500">Mic</span>
              <div className="h-2 w-24 bg-gray-200 rounded overflow-hidden" title="Mic level">
                <div className="h-full bg-green-500 transition-[width]" style={{ width: `${Math.round(micLevel * 100)}%` }} />
              </div>
            </div>
            {stage === 'recording' ? (
              <button onClick={handlePause} className="px-3 py-2 bg-gray-200 rounded hover:bg-gray-300">Pause</button>
            ) : (
              <button onClick={handleResume} className="px-3 py-2 bg-gray-200 rounded hover:bg-gray-300">Resume</button>
            )}
            <button onClick={toggleMicMute} className="px-3 py-2 bg-gray-200 rounded hover:bg-gray-300">
              {isMicMuted ? 'Unmute Mic' : 'Mute Mic'}
            </button>
            <button onClick={handleStop} className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700">Stop</button>
            <button onClick={handleDiscard} className="px-3 py-2 bg-gray-100 rounded hover:bg-gray-200">Cancel</button>
          </div>
        </div>
        </>
      )}

      {stage === 'preview' && (
        <div>
          <div className="mb-3">
            <input
              className="border rounded px-3 py-2 w-full max-w-md"
              placeholder="Recording title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <video ref={previewVideoRef} controls className="w-full max-h-[60vh] bg-black rounded" src={recordedBlob ? URL.createObjectURL(recordedBlob) : undefined} />
          <div className="mt-3 flex flex-wrap gap-2 items-center">
            <button onClick={saveToSupabase} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Save</button>
            <button onClick={handleDiscard} className="px-4 py-2 bg-gray-100 rounded hover:bg-gray-200">Discard</button>
            <button
              onClick={() => {
                try {
                  const v = previewVideoRef.current;
                  if (!v) return;
                  const c = document.createElement('canvas');
                  c.width = v.videoWidth || 1280;
                  c.height = v.videoHeight || 720;
                  const ctx = c.getContext('2d');
                  if (!ctx) return;
                  ctx.drawImage(v, 0, 0, c.width, c.height);
                  const url = c.toDataURL('image/png');
                  setSelectedThumbDataUrl(url);
                  toast.success('Thumbnail captured from current frame');
                } catch (e) {
                  toast.error('Failed to capture thumbnail');
                }
              }}
              className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
            >
              Use current frame as thumbnail
            </button>
            {selectedThumbDataUrl && (
              <span className="text-xs text-green-700">Custom thumbnail selected</span>
            )}
          </div>
        </div>
      )}

      {stage === 'saving' && (
        <div className="flex items-center justify-center py-10 text-gray-600">Saving…</div>
      )}

      {/* Hidden canvas and video elements for compositing */}
      <div style={{ position: 'fixed', left: 0, top: 0, width: 1, height: 1, opacity: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: -1 }}>
        <canvas ref={canvasRef} />
        <video ref={screenVideoRef} playsInline muted autoPlay />
        <video ref={camVideoRef} playsInline muted autoPlay />
      </div>

      <div className="mt-3 text-xs text-gray-500">
        Max duration: {Math.round(maxDurationSeconds / 60)} min. For best results, use Chrome/Edge.
      </div>

      <div className="mt-4">
        <button onClick={onCancel} className="text-sm text-gray-500 underline">Back</button>
      </div>

      {/* Draggable bubble handle overlay: visible when webcam enabled and not in saving/preview */}
      {(showWebcam && (stage === 'idle' || stage === 'recording' || stage === 'paused')) && (
        (() => {
          // Compute a viewport-based approximate position corresponding to current corner + offsets
          const margin = 96; // px from edges as a visual anchor
          let baseX = margin;
          let baseY = margin;
          if (bubbleCorner.includes('right')) baseX = viewport.w - margin;
          if (bubbleCorner.includes('bottom')) baseY = viewport.h - margin;
          const x = Math.max(32, Math.min(viewport.w - 32, baseX + (bubbleOffsetPct.x * viewport.w)));
          const y = Math.max(32, Math.min(viewport.h - 32, baseY + (bubbleOffsetPct.y * viewport.h)));
          const onMouseDown: React.MouseEventHandler<HTMLDivElement> = (e) => {
            e.preventDefault();
            setIsDragging(true);
            dragLastPointRef.current = { x: e.clientX, y: e.clientY };
          };
          const onTouchStart: React.TouchEventHandler<HTMLDivElement> = (e) => {
            const t = e.touches[0];
            if (!t) return;
            setIsDragging(true);
            dragLastPointRef.current = { x: t.clientX, y: t.clientY };
          };
          return (
            <div
              aria-label="Drag to reposition webcam bubble"
              title="Drag to reposition webcam bubble"
              onMouseDown={onMouseDown}
              onTouchStart={onTouchStart}
              onDoubleClick={() => {
                const order: BubbleCorner[] = ['bottom-right', 'bottom-left', 'top-left', 'top-right'];
                const idx = order.indexOf(bubbleCorner);
                const next = order[(idx + 1) % order.length];
                setBubbleCorner(next);
                localStorage.setItem('rec.bubbleCorner', next);
              }}
              style={{
                position: 'fixed',
                left: `${x}px`,
                top: `${y}px`,
                width: 56,
                height: 56,
                transform: 'translate(-50%, -50%)',
                borderRadius: '9999px',
                border: '2px dashed rgba(59,130,246,0.9)',
                background: 'rgba(59,130,246,0.08)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                cursor: isDragging ? 'grabbing' : 'grab',
                zIndex: 50,
                opacity: (showHandle || isDragging) ? 1 : 0,
                transition: 'opacity 200ms ease',
              }}
            />
          );
        })()
      )}
    </div>
  );
};

export default ScreenShareRecorder;
